import { useState } from 'react';
import { generateTestPlan } from '../services/api';
import { TestPlanRequest, TestPlanResponse } from '../types/api';

const TestPlanGenerator = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TestPlanResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<TestPlanRequest>({
    topic: '',
    level: 'intermediate',
    duration: 60,
    features: [],
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await generateTestPlan(formData);
      if (response.success && response.data) {
        setResult(response.data);
      } else {
        setError(response.error || '生成測試計劃失敗');
      }
    } catch (err: any) {
      setError(err.message || '請求失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureChange = (feature: string, checked: boolean) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features!, feature]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        features: prev.features!.filter(f => f !== feature)
      }));
    }
  };

  const commonFeatures = [
    '音視頻通話',
    '螢幕分享',
    '白板功能',
    '文件分享',
    '聊天功能',
    '錄影功能',
    '投票功能',
    '分組討論'
  ];

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          測試計劃生成器
        </h1>
        <p className="text-gray-600">
          使用 Gemini AI 生成詳細的功能測試計劃
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* 表單 */}
        <div className="bg-white rounded-lg shadow p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                測試主題
              </label>
              <input
                type="text"
                value={formData.topic}
                onChange={(e) => setFormData(prev => ({ ...prev, topic: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="例：線上會議系統"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                測試難度
              </label>
              <select
                value={formData.level}
                onChange={(e) => setFormData(prev => ({ ...prev, level: e.target.value as any }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="beginner">初級</option>
                <option value="intermediate">中級</option>
                <option value="advanced">高級</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                預期測試時間（分鐘）
              </label>
              <input
                type="number"
                value={formData.duration}
                onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="15"
                max="240"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                需要測試的功能
              </label>
              <div className="grid grid-cols-2 gap-2">
                {commonFeatures.map((feature) => (
                  <label key={feature} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.features?.includes(feature) || false}
                      onChange={(e) => handleFeatureChange(feature, e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </label>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? '生成中...' : '生成測試計劃'}
            </button>
          </form>
        </div>

        {/* 結果顯示 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            生成結果
          </h3>

          {loading && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">正在生成測試計劃...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {result && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <p className="text-green-800">✅ 測試計劃生成成功！</p>
                <p className="text-sm text-green-600 mt-1">
                  生成時間: {new Date(result.generatedAt).toLocaleString()}
                </p>
              </div>
              
              <div className="border rounded-md p-4">
                <h4 className="font-medium text-gray-900 mb-2">測試計劃內容：</h4>
                <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-3 rounded">
                  {result.testPlan}
                </pre>
              </div>
            </div>
          )}

          {!loading && !error && !result && (
            <div className="text-center py-8 text-gray-500">
              填寫表單並點擊生成按鈕來創建測試計劃
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestPlanGenerator;